import { PublicKey, TransactionInstruction, SYSVAR_RENT_PUBKEY, SystemProgram } from '@solana/web3.js';
import { TOKEN_PROGRAM_ID, ASSOCIATED_TOKEN_PROGRAM_ID, getAssociatedTokenAddressSync } from "@solana/spl-token";
import { PROGRAM_ID as pid } from '../constants'
import { Buffer } from 'buffer'

// IDs from environment
const PROGRAM_ID = new PublicKey(pid!);
const TOKEN_PROGRAM_ID_PK = TOKEN_PROGRAM_ID;
const ASSOCIATED_TOKEN_PROGRAM_ID_PK = ASSOCIATED_TOKEN_PROGRAM_ID;
const SYSTEM_PROGRAM_ID = SystemProgram.programId;

// Instruction Discriminators from IDL:
const IX_DS = {
  claim_rewards: Buffer.from([4, 144, 132, 71, 116, 23, 151, 80]),
  close_user_stake_account: Buffer.from([147, 64, 122, 117, 107, 183, 156, 19]),
  create_user_stake_account: Buffer.from([179, 34, 161, 2, 154, 58, 57, 29]),
  initialize_pool: Buffer.from([95, 180, 10, 172, 84, 174, 232, 40]),
  initialize_pool_fee: Buffer.from([181, 216, 159, 146, 141, 209, 205, 190]),
  stake: Buffer.from([206, 176, 202, 18, 200, 209, 179, 108]),
  unstake: Buffer.from([90, 95, 107, 42, 205, 124, 50, 225]),
  update_rewards: Buffer.from([188, 38, 124, 42, 87, 77, 176, 90]),
  withdraw_fees: Buffer.from([198, 212, 171, 109, 144, 215, 174, 89]),
  sync_global_rewards: Buffer.from([113, 213, 1, 172, 244, 83, 129, 164]),
};

// SEED CONSTANTS from IDL:
// For pool: seeds = ["pool", monopoly_mint, roll_mint]
// For user_stake: seeds = ["user_stake", pool, user]

/**
 * Derive the pool PDA given monopolyMint and rollMint
 */
export function derivePoolPda(monopolyMint: PublicKey, rollMint: PublicKey): [PublicKey, number] {
  return PublicKey.findProgramAddressSync(
    [Buffer.from("pool3"), monopolyMint.toBuffer(), rollMint.toBuffer()],
    PROGRAM_ID
  );
}

/**
 * Derive the user stake PDA given pool PDA and user
 */
export async function deriveUserStakePda(poolPda: PublicKey, user: PublicKey): Promise<[PublicKey, number]> {
  return PublicKey.findProgramAddress(
    [Buffer.from("user_stake"), poolPda.toBuffer(), user.toBuffer()],
    PROGRAM_ID
  );
}

// --------------------- Instruction Builders ---------------------

export function createInitializePoolInstruction(
  admin: PublicKey,
  monopolyMint: PublicKey,
  rollMint: PublicKey,
  poolPda: PublicKey,
  initialRewardRate: number,
  initialFeePercentage: number
): TransactionInstruction {
  const [monopolyStakeVaultAddress] = PublicKey.findProgramAddressSync(
    [Buffer.from("stake_vault2"), poolPda.toBuffer()],
    PROGRAM_ID
  );

  const dataLayout = Buffer.alloc(8 + 8 + 1);
  // First 8 bytes = discriminator
  IX_DS.initialize_pool.copy(dataLayout, 0);

  // next 8 bytes: initial_reward_rate (u64 le)
  dataLayout.writeBigUInt64LE(BigInt(initialRewardRate), 8);
  // next 1 byte: initial_fee_percentage (u8)
  dataLayout.writeUInt8(initialFeePercentage, 16);

  const keys = [
    { pubkey: admin, isSigner: true, isWritable: true },
    { pubkey: monopolyMint, isSigner: false, isWritable: false },
    { pubkey: rollMint, isSigner: false, isWritable: true },
    { pubkey: poolPda, isSigner: false, isWritable: true },
    { pubkey: monopolyStakeVaultAddress, isSigner: false, isWritable: true },
    //{ pubkey: monopolyFeeVaultAddress, isSigner: false, isWritable: true },
    { pubkey: SYSTEM_PROGRAM_ID, isSigner: false, isWritable: false },
    { pubkey: TOKEN_PROGRAM_ID_PK, isSigner: false, isWritable: false },
    { pubkey: ASSOCIATED_TOKEN_PROGRAM_ID_PK, isSigner: false, isWritable: false },
    { pubkey: SYSVAR_RENT_PUBKEY, isSigner: false, isWritable: false },
  ];

  return new TransactionInstruction({
    keys,
    programId: PROGRAM_ID,
    data: dataLayout
  });
}

export function createInitializePoolFeeInstruction(
  admin: PublicKey,
  monopolyMint: PublicKey,
  poolPda: PublicKey,
): TransactionInstruction {
  const [monopolyFeeVaultAddress] = PublicKey.findProgramAddressSync(
    [Buffer.from("fee_vault6"), poolPda.toBuffer()],
    PROGRAM_ID
  );

  const dataLayout = Buffer.alloc(8 + 8 + 1);
  // First 8 bytes = discriminator
  IX_DS.initialize_pool_fee.copy(dataLayout, 0);

  const keys = [
    { pubkey: admin, isSigner: true, isWritable: true },
    { pubkey: monopolyMint, isSigner: false, isWritable: false },
    { pubkey: poolPda, isSigner: false, isWritable: true },
    { pubkey: monopolyFeeVaultAddress, isSigner: false, isWritable: true },
    { pubkey: SYSTEM_PROGRAM_ID, isSigner: false, isWritable: false },
    { pubkey: TOKEN_PROGRAM_ID_PK, isSigner: false, isWritable: false },
    { pubkey: ASSOCIATED_TOKEN_PROGRAM_ID_PK, isSigner: false, isWritable: false },
    { pubkey: SYSVAR_RENT_PUBKEY, isSigner: false, isWritable: false },
  ];

  return new TransactionInstruction({
    keys,
    programId: PROGRAM_ID,
    data: dataLayout
  });
}

export function createUserStakeAccountInstruction(
  user: PublicKey,
  poolPda: PublicKey,
  userStakePda: PublicKey
): TransactionInstruction {
  const data = Buffer.alloc(8);
  IX_DS.create_user_stake_account.copy(data, 0);

  const keys = [
    { pubkey: user, isSigner: true, isWritable: true },
    { pubkey: poolPda, isSigner: false, isWritable: false },
    { pubkey: userStakePda, isSigner: false, isWritable: true },
    { pubkey: SYSTEM_PROGRAM_ID, isSigner: false, isWritable: false }
  ];

  return new TransactionInstruction({
    keys,
    programId: PROGRAM_ID,
    data
  });
}

export function createStakeInstruction(
  user: PublicKey,
  poolPda: PublicKey,
  userStakePda: PublicKey,
  userMonopolyAccount: PublicKey,
  monopolyStakeVault: PublicKey,
  monopolyFeeVault: PublicKey,
  amount: number,
  rollMint: PublicKey,
  userRollAccount: PublicKey,
): TransactionInstruction {
  const data = Buffer.alloc(8 + 8);
  IX_DS.stake.copy(data, 0);
  data.writeBigUInt64LE(BigInt(amount), 8);

  const keys = [
    { pubkey: user, isSigner: true, isWritable: true },
    { pubkey: poolPda, isSigner: false, isWritable: true },
    { pubkey: userStakePda, isSigner: false, isWritable: true },
    { pubkey: userMonopolyAccount, isSigner: false, isWritable: true },
    { pubkey: monopolyStakeVault, isSigner: false, isWritable: true },
    { pubkey: monopolyFeeVault, isSigner: false, isWritable: true },
    { pubkey: rollMint, isSigner: false, isWritable: true },
    { pubkey: userRollAccount, isSigner: false, isWritable: true },
    { pubkey: TOKEN_PROGRAM_ID_PK, isSigner: false, isWritable: false }
  ];

  return new TransactionInstruction({
    keys,
    programId: PROGRAM_ID,
    data
  });
}

export function createUnstakeInstruction(
  user: PublicKey,
  poolPda: PublicKey,
  userStakePda: PublicKey,
  userRollAccount: PublicKey,
  userMonopolyAccount: PublicKey,
  monopolyStakeVault: PublicKey,
  rollMint: PublicKey,
  amount: number
): TransactionInstruction {
  const data = Buffer.alloc(8 + 8);
  IX_DS.unstake.copy(data, 0);
  data.writeBigUInt64LE(BigInt(amount), 8);

  const keys = [
    { pubkey: user, isSigner: true, isWritable: true },
    { pubkey: poolPda, isSigner: false, isWritable: true },
    { pubkey: userStakePda, isSigner: false, isWritable: true },
    { pubkey: userRollAccount, isSigner: false, isWritable: true },
    { pubkey: userMonopolyAccount, isSigner: false, isWritable: true },
    { pubkey: monopolyStakeVault, isSigner: false, isWritable: true },
    { pubkey: rollMint, isSigner: false, isWritable: true },
    { pubkey: TOKEN_PROGRAM_ID_PK, isSigner: false, isWritable: false },
  ];

  return new TransactionInstruction({
    keys,
    programId: PROGRAM_ID,
    data
  });
}

export function createClaimRewardsInstruction(
  user: PublicKey,
  poolPda: PublicKey,
  userStakePda: PublicKey,
  userRollAccount: PublicKey,
  rollMint: PublicKey
): TransactionInstruction {
  const data = Buffer.alloc(8);
  IX_DS.claim_rewards.copy(data, 0);

  const keys = [
    { pubkey: user, isSigner: true, isWritable: true },
    { pubkey: poolPda, isSigner: false, isWritable: true },
    { pubkey: userStakePda, isSigner: false, isWritable: true },
    { pubkey: userRollAccount, isSigner: false, isWritable: true },
    { pubkey: rollMint, isSigner: false, isWritable: true },
    { pubkey: TOKEN_PROGRAM_ID_PK, isSigner: false, isWritable: false },
  ];

  return new TransactionInstruction({
    keys,
    programId: PROGRAM_ID,
    data
  });
}

export function createUpdateRewardsInstruction(
  admin: PublicKey,
  poolPda: PublicKey,
  new_reward_rate: number | null,
  new_fee_percentage: number | null
): TransactionInstruction {
  // update_rewards takes two Option arguments. We must encode them as:
  // Option<u64>, Option<u8>.
  // We'll represent Option as: first a boolean (1 byte: 0 = None, 1 = Some),
  // then the value if Some.

  const data = Buffer.alloc(8 + 1 + 8 + 1 + 1);
  // Discriminator (8 bytes)
  IX_DS.update_rewards.copy(data, 0);

  let offset = 8;

  // new_reward_rate: Option<u64>
  if (new_reward_rate !== null && new_reward_rate !== undefined) {
    data.writeUInt8(1, offset); offset += 1;
    data.writeBigUInt64LE(BigInt(new_reward_rate), offset); offset += 8;
  } else {
    data.writeUInt8(0, offset); offset += 1;
    offset += 8; // space still reserved, even though None
  }

  // new_fee_percentage: Option<u8>
  if (new_fee_percentage !== null && new_fee_percentage !== undefined) {
    data.writeUInt8(1, offset); offset += 1;
    data.writeUInt8(new_fee_percentage, offset); offset += 1;
  } else {
    data.writeUInt8(0, offset); offset += 1;
    offset += 1;
  }

  const keys = [
    { pubkey: admin, isSigner: true, isWritable: true },
    { pubkey: poolPda, isSigner: false, isWritable: true },
  ];

  return new TransactionInstruction({
    keys,
    programId: PROGRAM_ID,
    data
  });
}

export function createSyncGlobalRewardsInstruction(
  admin: PublicKey,
  poolPda: PublicKey,
): TransactionInstruction {
  const data = Buffer.alloc(8);
  IX_DS.sync_global_rewards.copy(data, 0);

  const keys = [
    { pubkey: admin, isSigner: true, isWritable: true },
    { pubkey: poolPda, isSigner: false, isWritable: true },
  ];

  return new TransactionInstruction({
    keys,
    programId: PROGRAM_ID,
    data
  });
}

export function createWithdrawFeesInstruction(
  admin: PublicKey,
  poolPda: PublicKey,
  monopolyFeeVault: PublicKey,
  adminMonopolyAccount: PublicKey,
  amount: bigint
): TransactionInstruction {
  const data = Buffer.alloc(8 + 8);
  IX_DS.withdraw_fees.copy(data, 0);
  data.writeBigUInt64LE(amount, 8);

  const keys = [
    { pubkey: admin, isSigner: true, isWritable: true },
    { pubkey: poolPda, isSigner: false, isWritable: true },
    { pubkey: monopolyFeeVault, isSigner: false, isWritable: true },
    { pubkey: adminMonopolyAccount, isSigner: false, isWritable: true },
    { pubkey: TOKEN_PROGRAM_ID_PK, isSigner: false, isWritable: false },
  ];

  return new TransactionInstruction({
    keys,
    programId: PROGRAM_ID,
    data
  });
}

export function createCloseUserStakeAccountInstruction(
  user: PublicKey,
  userStakePda: PublicKey,
  poolPda: PublicKey
): TransactionInstruction {
  const data = Buffer.alloc(8);
  IX_DS.close_user_stake_account.copy(data, 0);

  const keys = [
    { pubkey: user, isSigner: true, isWritable: true },
    { pubkey: userStakePda, isSigner: false, isWritable: true },
    { pubkey: poolPda, isSigner: false, isWritable: false },
  ];

  return new TransactionInstruction({
    keys,
    programId: PROGRAM_ID,
    data
  });
}

